// <SwiperLibraryPicker> -- "Select a swiper" dialog shared by every content
// builder (Page Builder, Shop Builder, Tutorials, Email Marketing).
// The server decides what each user may pick (Family: every active swiper;
// others: their own swipers + the Family swipers shared with their role, if
// their role may place swipers in this context -- Animation Settings ->
// Swiper Access & Limits) -- nothing is filtered here.
// context: "pageBuilder" (Page / Shop Builder, Tutorials, Email) | "shop"
// (My Shop Pub main slider).
import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { FaSearch, FaSpinner } from "react-icons/fa";
import { elakayAutoText } from "../pageInside/dataElakay";
import { listSwiperLibrary } from "../auth/apiDynamicSwipers";
import { DevicePreview, SwiperThumb, errorMessage } from "./SwiperAdminUI";
import "./ElakaySwiperBlock.css";

const text = elakayAutoText;

const SwiperLibraryPicker = ({ show, selectedId = null, onPick, onClose, title, context = "pageBuilder" }) => {
  const [search, setSearch] = useState("");
  const [state, setState] = useState({ loading: false, error: null, swipers: [], reason: null });
  const [previewId, setPreviewId] = useState(null);
  const [device, setDevice] = useState("desktop");

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      listSwiperLibrary({ search, limit: 60, context })
        .then((data) => { if (!cancelled) setState({ loading: false, error: null, swipers: data.swipers || [], reason: data.reason || null }); })
        .catch((error) => { if (!cancelled) setState({ loading: false, error, swipers: [], reason: null }); });
    }, search ? 250 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [show, search, context]);

  const preview = useMemo(() => state.swipers.find((swiper) => swiper.id === previewId) || null, [state.swipers, previewId]);

  return (
    <Modal show={show} onHide={onClose} size="xl" scrollable className="dswa dswa-modal" aria-labelledby="esb-picker-title">
      <Modal.Header closeButton>
        <Modal.Title id="esb-picker-title">{title || text("h_esb_picker_title", "Select a swiper")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="dswa-inline-row esb-picker-search">
          <FaSearch aria-hidden="true" />
          <input
            className="dswa-input"
            type="search"
            value={search}
            placeholder={text("h_esb_picker_search", "Search the Swiper Library…")}
            aria-label={text("h_esb_picker_search_label", "Search swipers")}
            onChange={(event) => setSearch(event.target.value)}
          />
          {state.loading && <FaSpinner className="dswa-spin" aria-hidden="true" />}
        </div>
        {state.error && <div className="dswa-notice dswa-notice-error" role="alert">{errorMessage(state.error, text("h_esb_picker_error", "The Swiper Library could not be loaded."))}</div>}
        {!state.loading && !state.error && !state.swipers.length && (
          <p className="esb-picker-empty">{state.reason === "PLACEMENT_DISABLED"
            ? (context === "shop"
              ? text("h_esb_picker_disabled_shop", "Your role cannot use swipers in its shop yet. Developer can enable it in Animation Settings → Swiper Access & Limits.")
              : text("h_esb_picker_disabled_builder", "Your role cannot place swipers in the Page Builder yet. Developer can enable it in Animation Settings → Swiper Access & Limits."))
            : search
              ? text("h_esb_picker_none_search", "No swiper matches this search.")
              : text("h_esb_picker_none_mine", "No swiper available yet. Create one in the Swiper Manager.")}</p>
        )}
        <ul className="esb-picker-grid" aria-label={text("h_esb_picker_list", "Swiper Library")}>
          {state.swipers.map((swiper) => (
            <li key={swiper.id}>
              <div className={`esb-picker-card${swiper.id === selectedId ? " is-selected" : ""}`}>
                <SwiperThumb url={swiper.slides?.[0]?.imageUrl} alt="" aspectRatio={swiper.aspectRatio} slide={swiper.slides?.[0]} />
                <strong>{swiper.name}</strong>
                <div className="esb-picker-meta">
                  <span>{text("h_esb_slides_count", "{n} slide(s)", { n: swiper.slidesCount || swiper.slides?.length || 0 })}</span>
                  {swiper.source === "own" && !swiper.visibility && <span className="dswa-badge dswa-badge-active">{text("h_esb_own", "Your swiper")}</span>}
                  {swiper.source === "family" && !swiper.visibility && <span className="dswa-badge dswa-badge-info">{text("h_esb_family_shared", "Shared by Developer")}</span>}
                  {swiper.visibility && (
                    <span className="dswa-badge dswa-badge-info">
                      {swiper.visibility === "shared" ? text("h_esb_shared_roles", "Shared") : text("h_esb_private_only", "Private")}
                    </span>
                  )}
                  {swiper.id === selectedId && <span className="dswa-badge dswa-badge-active">{text("h_esb_in_use", "In use here")}</span>}
                </div>
                <div className="esb-picker-actions">
                  <button type="button" className="dswa-btn dswa-btn-sm" onClick={() => setPreviewId(swiper.id === previewId ? null : swiper.id)} aria-pressed={swiper.id === previewId ? "true" : "false"}>
                    {text("h_dsw_preview", "Preview")}
                  </button>
                  <button type="button" className="dswa-btn dswa-btn-sm dswa-btn-primary" onClick={() => onPick(swiper)}>
                    {text("h_esb_use", "Use this swiper")}
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
        {preview && (
          <div className="esb-picker-preview">
            <DevicePreview swiper={preview} device={device} onDeviceChange={setDevice} />
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default SwiperLibraryPicker;
